import { zodResolver } from '@hookform/resolvers/zod'
import { LogIn } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { useAuth } from '@/features/auth/auth-context'
import { Banner } from '@/shared/components/ui/banner'
import { BrandLogo } from '@/shared/components/common/brand-logo'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Field } from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'

import { AuthShell } from '../components/auth-shell'

const schema = z.object({
  login: z.string().min(1, 'Введите телефон или email'),
  password: z.string().min(1, 'Введите пароль'),
})

type LoginForm = z.infer<typeof schema>

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated } = useAuth()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const form = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      login: '',
      password: '',
    },
  })

  if (isAuthenticated) {
    return <Navigate replace to="/projects" />
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setErrorMessage(null)

    try {
      await login(values)
      const redirect = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname
      navigate(redirect || '/projects', { replace: true })
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      setErrorMessage(
        status === 429
          ? 'Слишком много попыток входа. Подождите немного и попробуйте снова.'
          : 'Неверный телефон/email или пароль. Проверьте данные или воспользуйтесь ссылкой «Забыли пароль?».',
      )
    }
  })

  return (
    <AuthShell>
      <Card className="w-full">
        <CardHeader>
          <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[var(--color-accent)]/15 text-[var(--color-accent)]">
            <BrandLogo className="h-6 w-6" />
          </div>
          <CardTitle>Вход в ИИ-Менеджер</CardTitle>
          <CardDescription>
            Управляйте проектами, диалогами и оценками в одном месте.
          </CardDescription>
        </CardHeader>

        <CardBody>
          <form className="space-y-4" onSubmit={onSubmit}>
            <Field error={form.formState.errors.login?.message} label="Телефон или email">
              {(props) => (
                <Input
                  placeholder="+7 (999) 123-45-67 или you@company.com"
                  type="text"
                  {...form.register('login')}
                  {...props}
                />
              )}
            </Field>

            <Field error={form.formState.errors.password?.message} label="Пароль">
              {(props) => (
                <Input
                  placeholder="••••••••"
                  type="password"
                  {...form.register('password')}
                  {...props}
                />
              )}
            </Field>

            {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}

            <Button className="w-full" loading={form.formState.isSubmitting} type="submit">
              <LogIn className="h-4 w-4" />
              Войти
            </Button>
          </form>

          <p className="mt-4 text-sm text-[var(--color-text-soft)]">
            <Link
              className="font-medium text-[var(--color-accent)] hover:underline"
              to="/forgot-password"
            >
              Забыли пароль?
            </Link>
          </p>

          <p className="mt-2 text-sm text-[var(--color-text-soft)]">
            Нет аккаунта?{' '}
            <Link className="font-medium text-[var(--color-accent)] hover:underline" to="/register">
              Зарегистрироваться
            </Link>
          </p>
        </CardBody>
      </Card>
    </AuthShell>
  )
}
